import React, {useEffect, useLayoutEffect, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import {Spinner} from 'react-bootstrap'

type MenuItem = {
  iconClass?: string
  label: React.ReactNode
  onClick: () => void
  danger?: boolean
  disabled?: boolean
  loading?: boolean
}

interface ActionMenuPortalProps {
  anchorEl: HTMLElement
  onClose: () => void
  items: MenuItem[]
  width?: number
}

const ActionMenuPortal: React.FC<ActionMenuPortalProps> = ({anchorEl, onClose, items, width = 200}) => {
  const [position, setPosition] = useState<{top: number; left: number}>({top: 0, left: 0})
  const menuRef = useRef<HTMLDivElement | null>(null)
  const closeTimerRef = useRef<number | null>(null)
  const [hoverIndex, setHoverIndex] = useState<number | null>(null)

  const scheduleClose = () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current)
    }
    closeTimerRef.current = window.setTimeout(() => onClose(), 180)
  }

  const cancelClose = () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }

  useLayoutEffect(() => {
    const rect = anchorEl.getBoundingClientRect()
    const initialTop = rect.bottom + 8
    const left = Math.min(window.innerWidth - width - 8, rect.right - width)
    setPosition({top: initialTop, left: Math.max(8, left)})
  }, [anchorEl, width])

  // After menu mounts, re-calc to flip to top if it overflows viewport bottom
  useEffect(() => {
    const recalc = () => {
      const rect = anchorEl.getBoundingClientRect()
      const menuH = menuRef.current?.offsetHeight || 0
      let top = rect.bottom + 8
      if (top + menuH > window.innerHeight) {
        top = Math.max(8, rect.top - menuH - 8)
      }
      const left = Math.min(window.innerWidth - width - 8, rect.right - width)
      setPosition({top, left: Math.max(8, left)})
    }
    recalc()
    window.addEventListener('resize', recalc)
    window.addEventListener('scroll', recalc, true)
    // Hover intent across anchor/menu
    const onAnchorEnter = () => cancelClose()
    const onAnchorLeave = () => scheduleClose()
    anchorEl.addEventListener('mouseenter', onAnchorEnter)
    anchorEl.addEventListener('mouseleave', onAnchorLeave)
    return () => {
      window.removeEventListener('resize', recalc)
      window.removeEventListener('scroll', recalc, true)
      anchorEl.removeEventListener('mouseenter', onAnchorEnter)
      anchorEl.removeEventListener('mouseleave', onAnchorLeave)
    }
  }, [anchorEl, width, onClose])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node
      if (menuRef.current && !menuRef.current.contains(target) && !anchorEl.contains(target)) {
        onClose()
      }
    }
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEsc)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEsc)
    }
  }, [onClose, anchorEl])

  const menu = (
    <div
      ref={menuRef}
      style={{
        position: 'fixed',
        top: position.top,
        left: position.left,
        width,
        zIndex: 100000,
        background: '#ffffff',
        borderRadius: 8,
        boxShadow: '0px 0px 50px 0px rgba(82, 63, 105, 0.15)',
        padding: '0.5rem 0',
      }}
      role='menu'
      onMouseEnter={cancelClose}
      onMouseLeave={scheduleClose}
    >
      {items.map((item, idx) => {
        const isHover = hoverIndex === idx && !item.disabled && !item.loading
        const bg = isHover ? 'rgba(121, 80, 242, 0.08)' : 'transparent'
        const color = item.disabled || item.loading
          ? '#A1A5B7'
          : item.danger
          ? '#f1416c'
          : isHover
          ? '#7950F2'
          : '#3F4254'
        const iconColor = item.disabled || item.loading
          ? '#A1A5B7'
          : item.danger
          ? '#f1416c'
          : color
        return (
        <div
          key={idx}
          className='menu-item px-3'
          style={{
            padding: '0.25rem 0.75rem',
            backgroundColor: bg,
            borderRadius: 6,
            opacity: item.disabled || item.loading ? 0.6 : 1,
          }}
          onMouseEnter={() => !item.disabled && !item.loading && setHoverIndex(idx)}
          onMouseLeave={() => setHoverIndex(null)}
        >
          <span
            className={`menu-link px-3 d-flex align-items-center`}
            onClick={() => {
              if (item.disabled || item.loading) return
              try {
                item.onClick()
              } finally {
                if (!item.loading) {
                  onClose()
                }
              }
            }}
            role='menuitem'
            style={{
              cursor: item.disabled || item.loading ? 'not-allowed' : 'pointer',
              color,
            }}
          >
            {item.loading ? (
              <span
                className={`svg-icon svg-icon-2 me-3`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  animation: 'duplicatePulse 1.5s ease-in-out infinite',
                }}
              >
                <Spinner
                  size='sm'
                  style={{
                    width: '14px',
                    height: '14px',
                    borderWidth: '2px',
                    borderColor: '#7950F2',
                    borderRightColor: 'transparent',
                  }}
                />
              </span>
            ) : item.iconClass ? (
              <span className={`svg-icon svg-icon-2 me-3`}>
                <i className={`${item.iconClass}`} style={{color: iconColor}}></i>
              </span>
            ) : null}
            <span>{item.label}</span>
          </span>
        </div>
      )})}
    </div>
  )

  return createPortal(menu, document.body)
}

export default ActionMenuPortal


